import noop from "lodash/noop";
import { ChatConversation } from "PFApp/components/chat/chat_conversation";
import { ChatParticipants } from "PFApp/components/chat/chat_participants";
import { ChatState } from "PFApp/components/chat/chat_states";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { Conversation, CurrentProfile, PermissionRule, UnknownError } from "PFTypes";
import { useTranslation } from "react-i18next";

import { ChatAttachmentItem } from "./chat_attachment_input";
import { ChatMessagesData } from "./chat_types";

const getSubtitle = (isFollowing: boolean, t: (key: string) => string) =>
  t(isFollowing ? "following" : "notFollowing");
const canShowMentionables = (conversation: Conversation, isPermittedTo: (rule: PermissionRule) => boolean) =>
  conversation.kind === "primary" && isPermittedTo(PermissionRule.ProfileList);
const getOwnerId = (conversation: Conversation) => conversation.owner?.id as number;

type ChatAttachmentHandlers = {
  onAttachmentRemove: (attachment: ChatAttachmentItem) => void;
  onAttachmentSaved: (attachment: ChatAttachmentItem) => void;
  onAttachmentSaveFailed: (error: UnknownError) => void;
  onClearAttachments: () => void;
};

type ChatStatePanelHandlers = {
  onLoadPreviousMessages?: () => void;
  onMinimize: (event?: React.SyntheticEvent) => void;
  onOpenParticipants: () => void;
  onCloseParticipants: () => void;
};

type ChatStatePanelProps = {
  chatState: ChatState;
  conversation: Conversation;
  currentProfile: CurrentProfile;
  isPrivate: boolean;
  isFollowing: boolean;
  emptyMessage: string;
  messages?: ChatMessagesData;
  attachments: ChatAttachmentItem[];
  isFetchingPreviousMessages?: boolean;
  attachmentHandlers: ChatAttachmentHandlers;
  handlers: ChatStatePanelHandlers;
};

export const ChatStatePanel = ({
  chatState,
  conversation,
  currentProfile,
  isPrivate,
  isFollowing,
  emptyMessage,
  messages,
  attachments,
  isFetchingPreviousMessages,
  attachmentHandlers,
  handlers
}: ChatStatePanelProps) => {
  const { t } = useTranslation("core", { keyPrefix: "chat.conversation.subtitle" });
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const { onLoadPreviousMessages = noop, onMinimize, onOpenParticipants, onCloseParticipants } = handlers;

  if (chatState === ChatState.Participants) {
    return (
      <ChatParticipants
        conversationId={conversation.id as number}
        withMessage={conversation.kind === "primary"}
        ownerId={getOwnerId(conversation)}
        isPrivate={isPrivate}
        handleClose={onCloseParticipants}
        handleMinimize={onMinimize}
      />
    );
  }

  return (
    <ChatConversation
      attachments={attachments}
      clearAttachments={attachmentHandlers.onClearAttachments}
      conversationId={conversation.id}
      currentProfile={currentProfile}
      emptyMessage={emptyMessage}
      isPrivate={isPrivate}
      loading={chatState === ChatState.Loading}
      messages={messages}
      onAttachmentRemove={attachmentHandlers.onAttachmentRemove}
      onAttachmentSaved={attachmentHandlers.onAttachmentSaved}
      onAttachmentSaveFailed={attachmentHandlers.onAttachmentSaveFailed}
      onLoadPreviousMessages={onLoadPreviousMessages}
      onMinimize={onMinimize}
      onOpenParticipants={onOpenParticipants}
      showMentionables={canShowMentionables(conversation, isPermittedTo)}
      subtitle={getSubtitle(isFollowing, t)}
      isFetchingPreviousMessages={!!isFetchingPreviousMessages}
    />
  );
};
